import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Package, ArrowLeft, Share2, Edit, Plus } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icone Social SVG
const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    category: 'petshop',
    subcategory: '',
    price: '',
    stock: '',
    image_url: '',
    images: [],
    availability_date: '',
    available_on_order: false
  });
  const { addToCart } = useCart();
  const { user, token } = useAuth();

  // Definizione sottocategorie Pet Shop
  const PETSHOP_SUBCATEGORIES = [
    { value: 'cane', label: 'Cane', children: ['Cibo Umido', 'Cibo Secco'] },
    { value: 'gatto', label: 'Gatto', children: ['Cibo Secco', 'Cibo Umido'] }
  ];

  const getSubcategoryOptions = () => {
    const options = [];
    PETSHOP_SUBCATEGORIES.forEach(parent => {
      options.push({ value: parent.value, label: parent.label, isParent: true });
      parent.children.forEach(child => {
        options.push({ 
          value: `${parent.value}_${child.toLowerCase().replace(' ', '_')}`, 
          label: `${parent.label} - ${child}`,
          isParent: false 
        });
      });
    });
    return options;
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Prodotto non trovato');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    setProductForm({
      name: product.name,
      description: product.description,
      category: product.category,
      subcategory: product.subcategory || '',
      price: product.price.toString(),
      stock: product.stock.toString(),
      image_url: product.image_url || '',
      images: product.images || [],
      availability_date: product.availability_date || '',
      available_on_order: product.available_on_order || false
    });
    setNewImageUrl('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock)
      };

      await axios.put(
        `${API}/products/${product.id}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Prodotto aggiornato con successo!');
      setShowEditModal(false);
      fetchProduct(); // Ricarica il prodotto aggiornato
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      setAdding(true);
      await addToCart(product.id, quantity);
      
      // Messaggio diverso per prenotazione vs aggiunta normale
      if (product.stock === 0 && product.availability_date) {
        toast.success('Prodotto prenotato! Sarà disponibile dal ' + 
          new Date(product.availability_date).toLocaleDateString('it-IT'));
      } else {
        toast.success('Prodotto aggiunto al carrello!');
      }
    } catch (error) {
      toast.error(error.message || 'Errore');
    } finally {
      setAdding(false);
    }
  };
  
  // Verifica se il prodotto può essere prenotato
  const canPreorder = product && product.stock === 0 && product.availability_date;
  const isOutOfStock = product && product.stock === 0 && !product.availability_date && !product.available_on_order;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8 text-center">
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl overflow-hidden bg-gray-100">
            {/* Galleria Immagini */}
            {(() => {
              const allImages = [
                product.image_url,
                ...(product.images || [])
              ].filter(Boolean);
              
              if (allImages.length === 0) {
                return (
                  <div className="w-full aspect-square flex items-center justify-center">
                    <Package className="h-32 w-32 text-gray-400" />
                  </div>
                );
              }
              
              return (
                <div className="space-y-3">
                  {/* Immagine principale */}
                  <div className="relative w-full aspect-square overflow-hidden rounded-lg bg-gray-100">
                    <img
                      src={allImages[selectedImage] || allImages[0]}
                      alt={product.name}
                      className="absolute inset-0 w-full h-full object-contain"
                      style={{ objectPosition: 'center' }}
                    />
                  </div>
                  
                  {/* Miniature */}
                  {allImages.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {allImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImage === index 
                              ? 'border-rose-500 ring-2 ring-rose-200' 
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          <img
                            src={img}
                            alt={`${product.name} ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div>
            <div className="mb-4">
              <span className="text-sm font-semibold px-3 py-1 rounded-full bg-rose-100 text-rose-800">
                {product.category === 'petshop' && 'Pet Shop'}
                {product.category === 'giardinaggio' && 'Giardinaggio'}
                {product.category === 'varie' && 'Varie'}
              </span>
            </div>

            <h1 className="text-4xl font-extrabold mb-4" data-testid="product-detail-name">
              {product.name}
              {user?.is_admin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditModal}
                  className="ml-4 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  data-testid="edit-product-button"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Modifica
                </Button>
              )}
            </h1>
            <p className="text-xl text-gray-600 mb-4">{product.description}</p>

            {/* Pulsanti Condivisione Social */}
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Share2 className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-semibold text-gray-600">Condividi</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Facebook */}
                <button
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors"
                  title="Condividi su Facebook"
                >
                  <FacebookIcon />
                  <span className="text-sm font-medium hidden sm:inline">Facebook</span>
                </button>

                {/* WhatsApp */}
                <button
                  onClick={() => {
                    const text = encodeURIComponent(`${product.name} - €${product.price.toFixed(2)}\n${window.location.href}`);
                    window.open(`https://wa.me/?text=${text}`, '_blank');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-lg hover:bg-[#20BD5A] transition-colors"
                  title="Condividi su WhatsApp"
                >
                  <WhatsAppIcon />
                  <span className="text-sm font-medium hidden sm:inline">WhatsApp</span>
                </button>

                {/* Twitter/X */}
                <button
                  onClick={() => {
                    const text = encodeURIComponent(`${product.name} - €${product.price.toFixed(2)}`);
                    const url = encodeURIComponent(window.location.href);
                    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                  title="Condividi su X (Twitter)"
                >
                  <TwitterIcon />
                  <span className="text-sm font-medium hidden sm:inline">X</span>
                </button>

                {/* Telegram */}
                <button
                  onClick={() => {
                    const text = encodeURIComponent(`${product.name} - €${product.price.toFixed(2)}`);
                    const url = encodeURIComponent(window.location.href);
                    window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0088CC] text-white rounded-lg hover:bg-[#0077B5] transition-colors"
                  title="Condividi su Telegram"
                >
                  <TelegramIcon />
                  <span className="text-sm font-medium hidden sm:inline">Telegram</span>
                </button>

                {/* Email */}
                <button
                  onClick={() => {
                    const subject = encodeURIComponent(`Guarda questo prodotto: ${product.name}`);
                    const body = encodeURIComponent(`${product.name}\nPrezzo: €${product.price.toFixed(2)}\n\n${product.description}\n\nVedi qui: ${window.location.href}`);
                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  title="Invia per Email"
                >
                  <EmailIcon />
                  <span className="text-sm font-medium hidden sm:inline">Email</span>
                </button>

                {/* Instagram Stories */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success(
                      <div>
                        <p className="font-semibold">Link copiato! 📋</p>
                        <p className="text-sm">Incollalo nelle tue Instagram Stories</p>
                      </div>,
                      { duration: 4000 }
                    );
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded-lg hover:opacity-90 transition-opacity"
                  title="Condividi su Instagram Stories"
                >
                  <InstagramIcon />
                  <span className="text-sm font-medium hidden sm:inline">Stories</span>
                </button>

                {/* Copia Link */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link copiato!');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors"
                  title="Copia Link"
                >
                  <CopyIcon />
                  <span className="text-sm font-medium hidden sm:inline">Copia</span>
                </button>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-4xl font-bold text-emerald-600 mb-2" data-testid="product-detail-price">
                €{product.price.toFixed(2)}
              </p>
              <div data-testid="product-detail-stock">
                {product.stock > 0 ? (
                  <p className="text-green-600 font-semibold text-lg">
                    {product.stock} disponibili
                  </p>
                ) : (
                  <>
                    {product.availability_date ? (
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                        <p className="text-blue-700 font-semibold">
                          📅 Disponibile dal {new Date(product.availability_date).toLocaleDateString('it-IT', { 
                            day: '2-digit', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </p>
                        <p className="text-blue-600 text-sm mt-1">
                          Puoi prenotarlo contattandoci
                        </p>
                      </div>
                    ) : product.available_on_order ? (
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
                        <p className="text-amber-700 font-semibold">
                          📦 Disponibile su ordinazione
                        </p>
                        <p className="text-amber-600 text-sm mt-1">
                          Contattaci per ordinare questo prodotto
                        </p>
                      </div>
                    ) : (
                      <p className="text-red-600 font-semibold text-lg">
                        Esaurito
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {product.stock > 0 && (
              <div className="flex items-center gap-4 mb-6">
                <label className="font-semibold">Quantità:</label>
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 hover:bg-gray-100"
                    data-testid="quantity-decrease"
                  >
                    -
                  </button>
                  <span className="px-6 py-2 font-semibold" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="px-4 py-2 hover:bg-gray-100"
                    data-testid="quantity-increase"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Pulsante Aggiungi al Carrello / Prenota */}
            {canPreorder ? (
              <Button
                onClick={handleAddToCart}
                disabled={adding}
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full font-bold px-8 py-6"
                data-testid="preorder-detail"
              >
                📅 {adding ? 'Prenotazione...' : 'Prenota Prodotto'}
              </Button>
            ) : (
              <Button
                onClick={handleAddToCart}
                disabled={isOutOfStock || adding}
                className="w-full md:w-auto bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold px-8 py-6"
                data-testid="add-to-cart-detail"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                {isOutOfStock ? 'Prodotto Esaurito' : adding ? 'Aggiunta...' : 'Aggiungi al Carrello'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Modal Modifica Prodotto - Solo Admin */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-product-modal">
          <DialogHeader className="sticky top-0 bg-white pb-2 z-10">
            <DialogTitle>Modifica Prodotto</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4 pb-4">
            <div>
              <Label htmlFor="edit-name">Nome</Label>
              <Input
                id="edit-name"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-description">Descrizione</Label>
              <Textarea
                id="edit-description"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-category">Categoria</Label>
              <Select
                value={productForm.category}
                onValueChange={(value) => setProductForm({ ...productForm, category: value, subcategory: '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="petshop">Pet Shop</SelectItem>
                  <SelectItem value="giardinaggio">Giardinaggio</SelectItem>
                  <SelectItem value="varie">Varie</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sottocategoria - Solo per Pet Shop */}
            {productForm.category === 'petshop' && (
              <div>
                <Label htmlFor="edit-subcategory">Sottocategoria</Label>
                <Select
                  value={productForm.subcategory || 'none'}
                  onValueChange={(value) => setProductForm({ ...productForm, subcategory: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona sottocategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna sottocategoria</SelectItem>
                    {getSubcategoryOptions().map((sub) => (
                      <SelectItem 
                        key={sub.value} 
                        value={sub.value}
                      >
                        {sub.isParent ? `🐾 ${sub.label}` : `  └ ${sub.label.split(' - ')[1]}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-price">Prezzo (€)</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-stock">Stock</Label>
                <Input
                  id="edit-stock"
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Immagini Prodotto</Label>
              <div className="space-y-3">
                {/* Immagine principale */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-700 mb-2">📷 Immagine Principale</p>
                  <Input
                    type="url"
                    placeholder="URL immagine principale"
                    value={productForm.image_url}
                    onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                  />
                  {productForm.image_url && (
                    <img
                      src={productForm.image_url}
                      alt="Principale"
                      className="w-20 h-20 object-cover rounded-lg border mt-2"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>

                {/* Immagini aggiuntive */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold text-blue-700 mb-2">🖼️ Immagini Aggiuntive</p>
                  
                  {productForm.images && productForm.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {productForm.images.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={img}
                            alt={`Immagine ${index + 1}`}
                            className="w-16 h-16 object-cover rounded-lg border"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=Err'; }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = productForm.images.filter((_, i) => i !== index);
                              setProductForm({ ...productForm, images: newImages });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="Inserisci URL immagine aggiuntiva"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        if (newImageUrl.trim()) {
                          setProductForm({
                            ...productForm,
                            images: [...(productForm.images || []), newImageUrl.trim()]
                          });
                          setNewImageUrl('');
                        }
                      }}
                      disabled={!newImageUrl.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-gray-700 mb-3">Gestione Prodotto Esaurito</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-available-on-order"
                    checked={productForm.available_on_order}
                    onCheckedChange={(checked) =>
                      setProductForm({ ...productForm, available_on_order: checked })
                    }
                  />
                  <Label htmlFor="edit-available-on-order" className="text-sm cursor-pointer">
                    Disponibile su ordinazione
                  </Label>
                </div>

                <div>
                  <Label htmlFor="edit-availability-date">Data Disponibilità Futura</Label>
                  <Input
                    id="edit-availability-date"
                    type="date"
                    value={productForm.availability_date}
                    onChange={(e) => setProductForm({ ...productForm, availability_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-4 mt-4 border-t">
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold py-6"
              >
                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}